import { findIndex } from "lodash";

import { FieldRequestTypeEnum } from "@krea/shared/types/fieldRequest";

import {
  FieldRequestFieldEnum,
  type IFieldRequest,
} from "../types/fieldRequest";

/**
 * The selected offer's post-select to-do rows. Everything that treats the
 * selected response's field requests as "what the customer still owes" goes
 * through this. The rest of that response's field requests (already-handled
 * collateral, rows added after selection) are summary-only.
 */
export const getPostSelectFieldRequests = (fieldRequests: IFieldRequest[]) =>
  fieldRequests.filter(({ isVisibleToCustomer }) => isVisibleToCustomer);

export const getFilteredFieldRequests = (
  fieldsToFilter: string[] = [],
  fieldRequests: IFieldRequest[] = [],
) => {
  const result: IFieldRequest[] = [];

  fieldsToFilter.forEach((field) => {
    fieldRequests.forEach((item) => {
      if ((item.field as string) === field) {
        result.push(item);
      }
    });
  });

  return result;
};

export const getNextRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: FieldRequestFieldEnum,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => request.field === field,
  );
  const nextStep =
    stepIndex !== triageFieldsResult.length - 1 ? stepIndex + 1 : null;

  return nextStep ? triageFieldsResult[nextStep] : undefined;
};

export const getPreviousRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: FieldRequestFieldEnum,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => request.field === field,
  );
  const previousStep = stepIndex !== 0 ? stepIndex - 1 : null;

  return previousStep != null ? triageFieldsResult[previousStep] : undefined;
};

/**
 * The field requests the customer still has to act on. Collateral rows don't
 * count (guarantors excepted), nor do rows the lender collects itself. A
 * submitted row only counts when `shouldConsiderLenderComment` is set and the
 * lender has commented on it.
 */
export const getFieldRequestsToUpdate = (
  fieldRequests: IFieldRequest[],
  shouldConsiderLenderComment: boolean,
) =>
  // If changes are made here, also look on the backend, to check that the logic for LenderResponse.unsubmittedFieldRequests is the same
  // (not taking the lenderComment into account, which is fine).
  fieldRequests.filter(
    ({ lenderComment, isCollectedByLender, isSubmitted, field, type }) => {
      if (
        type === FieldRequestTypeEnum.COLLATERAL &&
        field !== FieldRequestFieldEnum.guarantors
      ) {
        return false; // don't consider collateral field requests (with the exception of guarantors)
      }
      // If the field request is collected by lender, we don't want to consider it
      if (isCollectedByLender) return false;

      if (isSubmitted) {
        if (shouldConsiderLenderComment) {
          // only consider a submitted FR if there is a lenderComment and we want to consider it
          if (lenderComment) {
            return true;
          }
          return false;
        }
        // submitted FRs will not be considered if we don't want to consider lenderComment
        return false;
      }
      // unsubmitted FRs will be considered if they are not collected by lender
      return true;
    },
  );

// return true if there is at least one field request to update
export const hasFieldRequestsToUpdate = (
  fieldRequests: IFieldRequest[],
  shouldConsiderLenderComment: boolean,
) =>
  getFieldRequestsToUpdate(fieldRequests, shouldConsiderLenderComment).length >
  0;
